import { useEffect, useState } from 'react'

import { LoginScreen } from '@/components/LoginScreen'
import { GuardianBanner } from '@/components/GuardianBanner'
import { OpenPositionsBar, useOpenPositions } from '@/components/OpenPositionsBar'
import { PositionsPanel } from '@/components/PositionsPanel'
import { SettingsScreen } from '@/screens/SettingsScreen'
import { StatsScreen } from '@/screens/StatsScreen'
import { StatusBar } from '@/components/StatusBar'
import { useLocalState } from '@/hooks/useLocalState'
import { CalculatorScreen } from '@/screens/CalculatorScreen'
import { SESSION_EXPIRED, api, type Health, type SessionInfo } from '@/lib/api'
import type { AccountsState, ExecutionMode, Instrument } from '@/lib/types'

/**
 * Enveloppe de l'application : bandeau d'état, sélecteur de mode, écran.
 *
 * À ce stade (étape 3) un seul écran existe, celui du mode CALCULATEUR. Les
 * modes CONFIRMATION et AUTO arriveront aux étapes 6 et 9 ; le sélecteur est
 * déjà en place pour que le mode courant soit visible en permanence dès
 * maintenant.
 */
export default function App() {
  const [health, setHealth] = useState<Health | null>(null)
  const [healthError, setHealthError] = useState<string | null>(null)
  const [mode, setMode] = useLocalState<ExecutionMode>('mode', 'calculator')
  const [tab, setTab] = useLocalState<'trade' | 'positions' | 'stats' | 'settings'>(
    'tab',
    'trade',
  )
  // Identité de la session : décide de l'affichage de l'administration.
  // Le serveur revérifie le rôle sur chaque route protégée.
  const [session, setSession] = useState<SessionInfo | null>(null)
  // `null` = on ne sait pas encore : on n'affiche ni l'application, ni l'écran
  // de connexion, pour éviter un clignotement au chargement.
  const [authenticated, setAuthenticated] = useState<boolean | null>(null)
  // Chargé une fois pour toute l'application : l'onglet Positions en a besoin
  // pour connaître le pip et les décimales de chaque paire.
  const [instruments, setInstruments] = useState<Instrument[]>([])
  /*
   * Comptes de trading de l'utilisateur, et celui qui est actif.
   *
   * Le compte actif décide de TOUT : prix, positions, statistiques, et le
   * compte où partent les ordres. Relu avec l'état du serveur, pour que la
   * pastille de liaison du sélecteur reste juste.
   */
  const [accountsState, setAccountsState] = useState<AccountsState | null>(null)
  /*
   * Retour de la page d'autorisation cTrader.
   *
   * Le serveur renvoie le navigateur ici avec `?ctrader=ok` ou
   * `?ctrader=erreur&detail=…`. On le lit une fois, on l'affiche dans les
   * réglages, et on nettoie l'adresse : un rechargement ne doit pas
   * réafficher un résultat vieux de dix minutes.
   */
  const [ctraderReturn] = useState<{ ok: boolean; detail: string } | null>(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get('ctrader')
    if (!result) return null
    window.history.replaceState(null, '', window.location.pathname)
    return { ok: result === 'ok', detail: params.get('detail') ?? '' }
  })

  useEffect(() => {
    if (!authenticated) return
    api
      .instruments()
      .then((response) => setInstruments(response.instruments))
      .catch(() => setInstruments([]))
  }, [authenticated])

  useEffect(() => {
    api
      .session()
      .then((result) => {
        setSession(result)
        setAuthenticated(result.authenticated)
      })
      .catch(() => setAuthenticated(false))

    // Une session qui expire en cours d'usage ramène à l'écran de connexion.
    const onExpired = () => setAuthenticated(false)
    window.addEventListener(SESSION_EXPIRED, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired)
  }, [])

  useEffect(() => {
    if (!authenticated) return
    let cancelled = false

    async function load() {
      try {
        const result = await api.health()
        if (cancelled) return
        setHealth(result)
        setHealthError(null)
      } catch (error) {
        if (cancelled) return
        setHealthError(error instanceof Error ? error.message : 'Erreur inconnue')
      }
    }

    void load()
    const timer = window.setInterval(load, 15_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [authenticated])

  useEffect(() => {
    if (!authenticated) {
      setAccountsState(null)
      return
    }
    let cancelled = false
    const load = () =>
      api
        .accounts()
        .then((state) => !cancelled && setAccountsState(state))
        .catch(() => undefined)
    void load()
    // Plus souvent que l'état général : juste après un changement de compte,
    // la pastille doit passer d'« en cours » à « connecté » sans attendre.
    const timer = window.setInterval(load, 5_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [authenticated])

  // Le retour d'autorisation cTrader s'affiche dans les réglages.
  useEffect(() => {
    if (ctraderReturn) setTab('settings')
  }, [ctraderReturn, setTab])

  /*
   * Changer d'onglet ramène en haut.
   *
   * Le contenu défile dans un cadre unique, partagé par les quatre onglets :
   * quitter un graphique parcouru jusqu'en bas pour ouvrir les positions
   * déposait au milieu de la liste, parfois sur du vide. On croit l'écran
   * vide, ou mal chargé. Chaque onglet doit s'ouvrir par son début.
   */
  useEffect(() => {
    document.querySelector<HTMLElement>('[data-scroll]')?.scrollTo({ top: 0 })
  }, [tab])

  // Un broker « disponible » suppose des identifiants ET une connexion vivante
  // — celle du compte ACTIF, dès qu'on le connaît. Tant qu'elle manque, aucun
  // ordre ne peut partir (§9).
  const activeAccount =
    accountsState?.accounts.find((account) => account.id === accountsState.active_id) ?? null
  const brokerAvailable = accountsState
    ? Boolean(activeAccount?.connected)
    : Boolean(health?.broker_configured && health?.broker_connected)

  /*
   * Positions ouvertes, lues depuis l'onglet Trader comme depuis la barre
   * d'onglets : le nombre sert de pastille, le résultat de bandeau. La page
   * Positions, elle, garde sa propre lecture — elle affiche bien plus.
   */
  /*
   * Compteur de rafraîchissement : il entre dans la clé du contenu, qui est
   * donc reconstruit — flux de prix, positions, graphique — à chaque
   * reconnexion demandée.
   */
  const [refreshCount, setRefreshCount] = useState(0)

  async function reconnect() {
    try {
      setAccountsState(await api.reconnectActive())
    } catch {
      /* L'écran se recharge quand même : c'est peut-être lui qui était figé. */
    } finally {
      setRefreshCount((count) => count + 1)
    }
  }

  /** Plafond du total en jeu, pour l'afficher à côté des positions. */
  const [maxTargetTotal, setMaxTargetTotal] = useState(0)
  useEffect(() => {
    if (!authenticated) return
    api
      .config()
      .then((config) => setMaxTargetTotal(config.risk_max_target_total))
      .catch(() => undefined)
  }, [authenticated])

  const openPositions = useOpenPositions(
    Boolean(authenticated) && brokerAvailable && (tab === 'trade' || tab === 'positions'),
  )

  // Devise du compte : celle du broker si connu, sinon celle saisie dans
  // l'écran de calcul (persistée sous la même clé).
  const accountCurrency = (() => {
    try {
      const stored = window.localStorage.getItem('reversetrader.account')
      return stored ? (JSON.parse(stored) as { currency?: string }).currency || 'EUR' : 'EUR'
    } catch {
      return 'EUR'
    }
  })()

  // Sécurité : si le broker devient indisponible alors qu'un mode d'envoi est
  // actif, on retombe sur le calculateur plutôt que de laisser un bouton
  // « Envoyer » qui ne mènerait nulle part.
  useEffect(() => {
    if (!brokerAvailable && mode !== 'calculator') setMode('calculator')
  }, [brokerAvailable, mode, setMode])

  if (authenticated === null) {
    return <div className="min-h-full bg-base" />
  }

  if (!authenticated) {
    return (
      <div className="min-h-full bg-base">
        <LoginScreen
          onSuccess={() => {
            void api.session().then(setSession)
            setAuthenticated(true)
          }}
        />
      </div>
    )
  }

  return (
    /*
      Coque d'application, pas page web.
      -------------------------------------------------------------------
      Trois zones fixes : l'état en haut, le contenu au milieu — seule zone
      qui défile —, la navigation en bas sur téléphone. C'est ce qui fait la
      différence entre « un site consulté au téléphone » et une application :
      la barre d'état ne part pas quand on fait défiler, et les onglets
      restent sous le pouce.

      Sur ordinateur, les onglets remontent en haut : la barre basse n'aurait
      aucun sens à la souris, et le pouce n'existe pas.
    */
    <div className="flex h-full flex-col overflow-hidden bg-base">
      <StatusBar
        health={health}
        error={healthError}
        mode={mode}
        accounts={accountsState}
        onAccountsChange={setAccountsState}
        onReconnect={reconnect}
        compact={
          <OpenPositionsBar
            compact
            positions={openPositions}
            onOpen={() => setTab('positions')}
          />
        }
      />

      {/* Onglets d'ordinateur, en haut. Masqués sur téléphone. */}
      <nav className="safe-x hidden shrink-0 border-b border-border bg-panel/95 backdrop-blur md:block">
        <div className="mx-auto flex w-full max-w-[1700px] px-3">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`min-h-touch px-8 text-[13px] font-semibold transition-colors ${
                tab === item.key
                  ? 'border-b-2 border-accent text-ink-strong'
                  : 'border-b-2 border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {item.label}
              {item.key === 'positions' && openPositions.length > 0 && (
                <span className="num ml-2 rounded-full bg-accent/20 px-1.5 text-[11px] text-accent">
                  {openPositions.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/*
        Largeur : l'application tenait dans 672 px, y compris sur un écran de
        1 920. Le graphique — l'outil principal — y était minuscule pendant que
        les deux tiers de l'écran restaient vides. La colonne unique reste la
        règle sur téléphone ; au-delà, la place disponible est utilisée.
      */}
      {/*
        Seule zone défilante de l'application. `data-scroll` sert de repère aux
        composants qui mesurent la place disponible (hauteur des graphiques) :
        sans lui, ils mesureraient par rapport à la fenêtre et se tromperaient
        dès que ce cadre est défilé.
      */}
      <main
        data-scroll
        className="safe-x min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2 md:px-3 md:py-3"
      >
        {/*
          Tout le contenu est RECONSTRUIT au changement de compte (`key`).
          C'est le moyen sûr de ne rien garder de l'ancien compte : prix,
          positions, bougies, ticket, flux de cotations — chacun repart de
          zéro sur le nouveau, au lieu d'afficher un instant les chiffres de
          l'autre compte à côté du nom du nouveau.
        */}
        <div
          key={`${accountsState?.active_id ?? 'aucun-compte'}:${refreshCount}`}
          className="mx-auto flex w-full max-w-[1700px] flex-col gap-2 md:gap-3"
        >
        {/*
          L'onglet Trader ne porte plus que le graphique et le ticket. Le mode
          d'exécution et le rappel de coupure automatique sont partis dans les
          Paramètres : ils se règlent une fois, et ils repoussaient le
          graphique vers le bas à chaque ouverture.
        */}
        {tab === 'trade' ? (
          <>
            {/* Sur ordinateur seulement : sur téléphone, ce résumé occupe la
                barre du haut, où il ne coûte aucune hauteur de graphique. */}
            <div className="hidden md:block">
              <OpenPositionsBar
                positions={openPositions}
                cap={maxTargetTotal}
                onOpen={() => setTab('positions')}
              />
            </div>
            <CalculatorScreen positions={openPositions} />
          </>
        ) : tab === 'positions' ? (
          <>
            <GuardianBanner brokerReady={Boolean(health?.broker_connected)} />
            <PositionsPanel
              brokerReady={Boolean(health?.broker_connected)}
              currency={accountCurrency}
              instruments={instruments}
            />
          </>
        ) : tab === 'stats' ? (
          <StatsScreen brokerReady={Boolean(health?.broker_connected)} />
        ) : (
          <SettingsScreen
            mode={mode}
            onChangeMode={setMode}
            brokerAvailable={brokerAvailable}
            brokerReady={Boolean(health?.broker_connected)}
            role={session?.role ?? null}
            username={session?.username ?? null}
            ctraderReturn={ctraderReturn}
            onAccountsChanged={() => {
              void api.accounts().then(setAccountsState).catch(() => undefined)
            }}
            onLogout={async () => {
              // On efface l'état local AVANT tout : même si l'appel échoue —
              // réseau coupé, serveur indisponible — l'utilisateur doit se
              // retrouver déconnecté, pas devant une application qui refuse
              // de le lâcher.
              setSession(null)
              setAuthenticated(false)
              setTab('trade')
              try {
                await api.logout()
              } catch {
                /* Le cookie expirera de lui-même. */
              }
            }}
          />
        )}
        </div>
      </main>

      {/* Navigation basse, à portée de pouce — la convention de toute
          application mobile. Elle disparaît sur ordinateur. */}
      <nav className="safe-bottom safe-x flex shrink-0 border-t border-border bg-panel md:hidden">
        {TABS.map((item) => {
          const active = tab === item.key
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              aria-current={active ? 'page' : undefined}
              className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${
                active ? 'text-accent' : 'text-ink-muted active:text-ink'
              }`}
            >
              {/* Repère d'onglet actif : la couleur seule se distingue mal
                  d'un simple survol, et ne suffit pas si l'on perçoit mal les
                  contrastes. */}
              {active && (
                <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-accent" />
              )}
              <item.Icon />
              <span className="flex items-center gap-1">
                {item.label}
                {item.key === 'positions' && openPositions.length > 0 && (
                  <span className="num rounded-full bg-accent/20 px-1 text-[9px] text-accent">
                    {openPositions.length}
                  </span>
                )}
              </span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}

/** Icône « graphique » : trois bougies. */
function ChartIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 4v16M6 8h0M18 4v16M12 4v16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.35"
      />
      <rect x="4" y="8" width="4" height="8" rx="1" fill="currentColor" />
      <rect x="10" y="5" width="4" height="11" rx="1" fill="currentColor" />
      <rect x="16" y="10" width="4" height="6" rx="1" fill="currentColor" />
    </svg>
  )
}

/** Icône « liste » : les positions ouvertes. */
function ListIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="3" rx="1.5" fill="currentColor" />
      <rect x="3" y="10.5" width="18" height="3" rx="1.5" fill="currentColor" opacity="0.7" />
      <rect x="3" y="16" width="12" height="3" rx="1.5" fill="currentColor" opacity="0.45" />
    </svg>
  )
}

/** Icône « statistiques » : trois barres de hauteurs croissantes. */
function StatsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="14" width="4.5" height="7" rx="1.5" fill="currentColor" opacity="0.45" />
      <rect x="9.75" y="9" width="4.5" height="12" rx="1.5" fill="currentColor" opacity="0.7" />
      <rect x="16.5" y="4" width="4.5" height="17" rx="1.5" fill="currentColor" />
    </svg>
  )
}

/** Icône « réglages » : une roue crantée simplifiée. */
function GearIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 3v2.2M12 18.8V21M21 12h-2.2M5.2 12H3M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6M18.4 18.4l-1.6-1.6M7.2 7.2 5.6 5.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Les destinations de l'application, partagées par les deux barres. */
const TABS = [
  { key: 'trade', label: 'Trader', Icon: ChartIcon },
  { key: 'positions', label: 'Positions', Icon: ListIcon },
  { key: 'stats', label: 'Stats', Icon: StatsIcon },
  { key: 'settings', label: 'Réglages', Icon: GearIcon },
] as const
